import {
  SupportedFiling,
  useBusinessEntitiesOfCompany,
  useComplianceMonitors,
} from '@commenda/api';
import { ComplianceMonitorCorporationInfoOnboardingStatus } from '@commenda/api/compliance-monitor/compliance-monitor.types';
import {
  CountryCode,
  FlagComponent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleCombobox,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { Country as CountryPkg } from 'country-state-city';
import { FormCard } from '../../../../form-builder/components/new-incorp-layout';
import { FilingFormData } from '../AddCustomFilingModal';
import { ComplianceCategoryEnumToText } from '../types';

interface FilingDetailsStepProps {
  formData: FilingFormData;
  onDataUpdate: (data: Partial<FilingFormData>) => void;
  needsEntitySelection?: boolean;
  companyId?: number;
  supportedFilings: SupportedFiling[];
  onCreateNew: () => void;
}

export const FilingDetailsStep = ({
  formData,
  onDataUpdate,
  needsEntitySelection,
  companyId,
  supportedFilings,
  onCreateNew,
}: FilingDetailsStepProps) => {
  const { data } = useBusinessEntitiesOfCompany(companyId || 0);
  const entities = data?.entities || [];

  const entityIds = entities.map((e: IBusinessEntity) => e.id);
  const { data: complianceMonitorsData } = useComplianceMonitors(entityIds);
  const complianceMonitors = complianceMonitorsData?.complianceMonitors || [];

  const complianceStatusMap = new Map(
    complianceMonitors.map((monitor) => [
      monitor.corporation.id,
      monitor.onboardingInformation?.status,
    ])
  );

  const availableEntities = entities.filter((entity: IBusinessEntity) => {
    const hasLegalName = entity.generalCorporationInfo?.legalName?.trim();
    const complianceStatus = complianceStatusMap.get(entity.id);
    const isComplianceCompleted =
      complianceStatus ===
      ComplianceMonitorCorporationInfoOnboardingStatus.COMPLETED;

    return hasLegalName && isComplianceCompleted;
  });

  const needsEntityFirst = needsEntitySelection && !formData.selectedEntityId;

  const handleSelectFiling = (compositeId: string) => {
    const filing = supportedFilings.find((f) => f.compositeId === compositeId);
    if (!filing) {
      onDataUpdate({
        complianceType: undefined,
        isCustom: false,
        requiresManualDueDate: false,
        filingName: '',
        category: '',
        firstDueDate: '',
      });
      return;
    }
    onDataUpdate({
      complianceType: filing.compositeId,
      isCustom: false,
      requiresManualDueDate: filing.requiresManualDueDate,
      filingName: filing.title,
      category: filing.category,
      frequency: filing.frequency,
      firstDueDate: '',
    });
  };

  return (
    <FormCard>
      {needsEntitySelection && companyId && (
        <div className="w-full">
          <Label>Entity</Label>
          <div className="mt-sm">
            <Select
              value={formData.selectedEntityId?.toString()}
              onValueChange={(value) =>
                onDataUpdate({ selectedEntityId: Number(value) })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Entity" />
              </SelectTrigger>
              <SelectContent>
                {availableEntities.map((entity: IBusinessEntity) => {
                  const country =
                    entity.generalCorporationInfo.incorporationCountry;
                  const name = entity.generalCorporationInfo.legalName;
                  const countryName =
                    CountryPkg.getCountryByCode(country)?.name ?? country;

                  return (
                    <SelectItem key={entity.id} value={entity.id.toString()}>
                      <div className="flex items-center gap-sm">
                        <FlagComponent
                          country={country as CountryCode}
                          countryName={countryName}
                          _className="w-4 h-4"
                        />
                        <span>{name}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <p className="txt-extra-small-regular text-text-tertiary mt-xs">
            Select the entity for which you want to add this filing.
          </p>
        </div>
      )}

      <div className="w-full">
        <Label>Filing</Label>
        <div className="mt-sm">
          {formData.isCustom ? (
            <Input fullWidth value={formData.filingName} disabled readOnly />
          ) : (
            <SingleCombobox
              options={supportedFilings.map((filing) => ({
                value: filing.compositeId,
                label: filing.title,
              }))}
              value={formData.complianceType ?? ''}
              setValue={handleSelectFiling}
              disabled={needsEntityFirst}
              placeholder="Search or type a filing name"
              searchPlaceholder="Search filings..."
              notFoundText="No matching filings in content."
              addItemConfig={{
                shouldAllowAdd: true,
                onAddItem: onCreateNew,
                placeholder: 'Create new filing',
              }}
              renderOption={({ option, onSelect }) => {
                const filing = supportedFilings.find(
                  (f) => f.compositeId === option.value
                );
                return (
                  <div
                    className="flex items-center justify-between gap-md px-3 py-2 cursor-pointer rounded-md hover:bg-bg-secondary"
                    onClick={() => onSelect(option.value)}
                  >
                    <span className="txt-small-regular text-text-primary">
                      {option.label}
                    </span>
                    {filing && (
                      <span className="txt-small-regular text-text-tertiary">
                        {ComplianceCategoryEnumToText[filing.category] ??
                          filing.category}
                      </span>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
        <p className="txt-extra-small-regular text-text-tertiary mt-xs">
          {formData.isCustom
            ? 'Custom filing. Review the details below, then add it.'
            : needsEntityFirst
              ? 'Select an entity to see the filings available in content.'
              : 'Pick from filings available for this entity, or type your own to add a custom filing.'}
        </p>
      </div>

      {formData.category && (
        <div className="w-full">
          <Label>Filing type</Label>
          <div className="mt-sm">
            <Select value={formData.category} disabled>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={formData.category}>
                  {ComplianceCategoryEnumToText[
                    formData.category as keyof typeof ComplianceCategoryEnumToText
                  ] ?? formData.category}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="txt-extra-small-regular text-text-tertiary mt-xs">
            The category this filing falls under.
          </p>
        </div>
      )}
    </FormCard>
  );
};
